import { router } from '@inertiajs/react'
import { useMemo, useState, useEffect } from 'react'
import { Loader2 } from 'lucide-react'
import { Text } from '@/components/design-system'
import { Button } from '@/components/ui/button'
import OnboardingLayout from '@/frontend/layouts/OnboardingLayout'
import UserInfoStep from './steps/UserInfoStep'
import OrganizationStep from './steps/OrganizationStep'
import GoalsStep from './steps/GoalsStep'
import { useBalloonAnimation } from '@/frontend/hooks/useBalloonAnimation'

type PageComponent = React.FC<OnboardingProps> & {
  layout?: (page: React.ReactElement) => React.ReactElement
}

interface OnboardingProps {
  user: { name: string | null; email: string; phone_number: string | null }
  organization: { name: string; slug: string } | null
  is_invited_user: boolean
}

interface FormData {
  name: string
  phone_number: string
  organization_name: string
  industry: string
  company_size: string
  role_in_company: string
  website_url: string
  use_cases: string[]
}

const GENERIC_EMAIL_DOMAINS = new Set([
  'gmail.com', 'hotmail.com', 'outlook.com', 'yahoo.com', 'yahoo.com.br',
  'uol.com.br', 'bol.com.br', 'terra.com.br', 'ig.com.br',
  'icloud.com', 'live.com', 'protonmail.com', 'me.com', 'msn.com',
])

function extractWebsiteFromEmail(email: string): string {
  const domain = email.split('@')[1]?.toLowerCase()
  if (!domain || GENERIC_EMAIL_DOMAINS.has(domain)) return ''
  return `https://${domain}`
}

const Show: PageComponent = ({ user, is_invited_user }) => {
  const [currentStep, setCurrentStep] = useState(1)
  const [submitting, setSubmitting] = useState(false)
  const { launchBalloons, cleanup } = useBalloonAnimation()

  const [formData, setFormData] = useState<FormData>({
    name: user.name || '',
    phone_number: user.phone_number || '',
    organization_name: '',
    website_url: extractWebsiteFromEmail(user.email),
    industry: '',
    company_size: '',
    role_in_company: '',
    use_cases: [],
  })

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanup()
    }
  }, [cleanup])

  // Build step sequence based on user type
  const stepDefinitions = useMemo(() => {
    const steps: { key: string; label: string }[] = [
      { key: 'user', label: 'Seus dados' },
    ]
    if (!is_invited_user) {
      steps.push({ key: 'org', label: 'Empresa' })
    }
    steps.push({ key: 'goals', label: 'Perfil' })
    steps.push({ key: 'done', label: '\u{1F389}' })
    return steps
  }, [is_invited_user])

  const totalSteps = stepDefinitions.length
  const currentStepKey = stepDefinitions[currentStep - 1]?.key

  // Per-step validation
  const canProceed = useMemo(() => {
    switch (currentStepKey) {
      case 'user':
        return formData.name.trim().length > 0
      case 'org':
        return formData.organization_name.trim().length > 0 && formData.website_url.trim().length > 0
      case 'goals':
        return formData.role_in_company.trim().length > 0
      case 'done':
        return true
      default:
        return false
    }
  }, [currentStepKey, formData])

  const updateField = <K extends keyof FormData>(field: K, value: FormData[K]) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  // Type-safe handlers for specific step types
  const handleUserInfoChange = (field: 'name' | 'phone_number', value: string) => {
    updateField(field, value)
  }

  const handleOrganizationChange = (field: 'organization_name' | 'website_url' | 'industry' | 'company_size', value: string) => {
    updateField(field, value)
  }

  const handleGoalsChange = (field: 'role_in_company' | 'use_cases', value: string | string[]) => {
    if (field === 'use_cases' && Array.isArray(value)) {
      updateField(field, value)
    } else if (field === 'role_in_company' && typeof value === 'string') {
      updateField(field, value)
    }
  }

  const goNext = () => {
    if (currentStep < totalSteps) {
      setCurrentStep((s) => s + 1)
    }
  }

  const goBack = () => {
    if (currentStep > 1) {
      setCurrentStep((s) => s - 1)
    }
  }


  const handleSubmit = () => {
    setSubmitting(true)
    launchBalloons()
    router.patch('/dashboard/onboarding', { ...formData }, {
      onFinish: () => setSubmitting(false),
    })
  }


  return (
    <div className="mt-8 md:mt-16">
      <div className="flex min-h-[520px] flex-col md:flex-row">
        {/* Sidebar - Junto-style vertical dot progress */}
        <div className="hidden w-48 shrink-0 pt-4 md:block">
          <nav className="relative flex flex-col gap-0" aria-label="Etapas da configuração">
            {stepDefinitions.map((def, idx) => {
              const stepNum = idx + 1
              const isActive = stepNum === currentStep
              const isCompleted = stepNum < currentStep
              const isLast = idx === stepDefinitions.length - 1

              return (
                <div key={def.key} className="relative flex items-start gap-3">
                  {/* Vertical line */}
                  {!isLast && (
                    <div
                      className="absolute left-[5px] top-[14px] w-[2px]"
                      style={{ height: 'calc(100% - 2px)' }}
                    >
                      <div className={`h-full w-full ${isCompleted ? 'bg-accent0' : 'bg-muted'}`} />
                    </div>
                  )}

                  {/* Dot */}
                  <div
                    className={`relative z-10 mt-[2px] h-3 w-3 shrink-0 rounded-full transition-colors ${
                      isActive
                        ? 'bg-gray-900'
                        : isCompleted
                          ? 'bg-accent0'
                          : 'bg-muted'
                    }`}
                  />

                  {/* Label */}
                  <button
                    type="button"
                    onClick={() => isCompleted && setCurrentStep(stepNum)}
                    disabled={!isCompleted}
                    className={`pb-6 text-sm transition-colors ${
                      isActive
                        ? 'font-semibold text-muted-foreground'
                        : isCompleted
                          ? 'cursor-pointer font-medium text-muted-foreground hover:text-muted-foreground'
                          : 'cursor-default font-medium text-muted-foreground'
                    }`}
                  >
                    {def.label}
                  </button>
                </div>
              )
            })}
          </nav>
        </div>

        {/* Mobile step indicator */}
        <div className="flex items-center justify-center py-3 md:hidden">
          <Text as="span" size="body-small" color="subdued">
            Etapa {currentStep} de {totalSteps}
          </Text>
        </div>

        {/* Main content */}
        <div className="flex flex-1 flex-col justify-between px-4 py-6 md:px-12 md:py-4">
          <div>
            {currentStepKey === 'user' && (
              <UserInfoStep
                data={{ name: formData.name, phone_number: formData.phone_number }}
                onChange={handleUserInfoChange}
              />
            )}

            {currentStepKey === 'org' && (
              <OrganizationStep
                data={{
                  organization_name: formData.organization_name,
                  website_url: formData.website_url,
                  industry: formData.industry,
                  company_size: formData.company_size,
                }}
                onChange={handleOrganizationChange}
              />
            )}

            {currentStepKey === 'goals' && (
              <GoalsStep
                data={{
                  role_in_company: formData.role_in_company,
                  use_cases: formData.use_cases,
                }}
                onChange={handleGoalsChange}
              />
            )}

            {currentStepKey === 'done' && (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <span className="text-6xl">🎉</span>
                <Text as="h1" size="display-small" className="mt-6 font-bold text-foreground">
                  Tudo pronto!
                </Text>
                <Text as="p" size="body-large" color="subdued" className="mt-3 max-w-md">
                  Sua conta está configurada. Vamos conhecer seus workers.
                </Text>
                <div className="mt-8">
                  <Button
                    type="button"
                    onClick={handleSubmit}
                    disabled={submitting}
                  >
                    {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                    Conhecer meus workers
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Navigation - hidden on done step */}
          {currentStepKey !== 'done' && (
            <div className="mt-10 flex items-center justify-between">
              <div>
                {currentStep > 1 && (
                  <Button type="button" variant="link" onClick={goBack}>
                    Voltar
                  </Button>
                )}
              </div>

              <div>
                <Button
                  type="button"
                  onClick={goNext}
                  disabled={!canProceed}
                >
                  Próximo
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

Show.layout = (page: React.ReactElement) => (
  <OnboardingLayout>{page}</OnboardingLayout>
)

export default Show
